import type { UserManual } from "./types.js";

export const FORTH_TUTORIAL: UserManual = {
  audience:
    "Students and hobbyists who have never used FORTH before and want a gradual introduction to stack thinking, editor workflow, interactive tools, and the full currently implemented AM8 FORTH surface.",
  description:
    "A long-form beginner course for the AM8 FORTH environment. The lessons progress from postfix stack basics to memory access, control flow, defining words, data-space tools, metaprogramming, interactive input, comments, and the optional REPL loop.",
  eyebrow: "FORTH Tutorial",
  highlights: [
    "Tutorial | 23 lessons",
    "Focus | every shipped FORTH construct in context",
    "Outcome | build, inspect, extend, and interact with real FORTH words"
  ],
  id: "forth-tutorial",
  title: "FORTH Programming for Beginners",
  sections: [
    {
      id: "forth-lesson-1",
      summary:
        "Enter FORTH mode, meet postfix notation, and learn the one question that keeps beginners grounded: what is on the stack right now?",
      title: "Lesson 1: Enter FORTH and Meet the Stack",
      bodyHtml: `
        <p>FORTH feels unusual at first because it is <strong>stack-based</strong>. Instead of writing an operator between values, you usually push values first and then apply a word that consumes them. This is postfix notation.</p>
        <p>Enter FORTH mode like this:</p>
        <pre><code>FORTH</code></pre>
        <p>The prompt changes to <code>F&gt;</code>. A tiny first example is:</p>
        <pre><code>2 3 + .</code></pre>
        <ol>
          <li><code>2</code> pushes 2.</li>
          <li><code>3</code> pushes 3.</li>
          <li><code>+</code> consumes the top two values and pushes the sum.</li>
          <li><code>.</code> prints the top value.</li>
        </ol>
        <p>The beginner habit that matters most is simple: keep asking <strong>what is on the stack right now?</strong> Once you track that, the language stops feeling mystical.</p>
      `
    },
    {
      id: "forth-lesson-2",
      summary:
        "Use the first essential stack words so you can duplicate, discard, and reorder values before you compute with them.",
      title: "Lesson 2: DUP, DROP, SWAP, OVER, and NIP",
      bodyHtml: `
        <p>A small group of stack words appears constantly in FORTH. Learn them early and many later examples become much easier to read.</p>
        <pre><code>5 DUP + .
10 20 SWAP . .
3 4 OVER + .
7 8 NIP .</code></pre>
        <ul>
          <li><code>DUP</code> duplicates the top value.</li>
          <li><code>DROP</code> discards the top value.</li>
          <li><code>SWAP</code> exchanges the top two values.</li>
          <li><code>OVER</code> copies the second value to the top.</li>
          <li><code>NIP</code> removes the second value while leaving the top one.</li>
        </ul>
        <p>These words do not "solve the problem" directly. They prepare the stack so the next word can solve it cleanly.</p>
      `
    },
    {
      id: "forth-lesson-3",
      summary:
        "Add the next layer of stack fluency with deeper rearrangement, indexed stack access, and simple inspection tools.",
      title: "Lesson 3: ROT, 2DUP, 2DROP, 2SWAP, 2OVER, 2ROT, PICK, ROLL, WITHIN, DEPTH, and .S",
      bodyHtml: `
        <p>Once two-stack-item programs feel comfortable, you can start using larger rearrangements and simple inspection helpers.</p>
        <pre><code>1 2 3 ROT .S
1 2 2DUP .S
1 2 3 4 2SWAP .S
$AAAA $BBBB 2DROP DEPTH .
1 2 3 4 2OVER .S
10 20 30 1 PICK .
1 2 3 2 ROLL .S
5 3 7 WITHIN .</code></pre>
        <ul>
          <li><code>ROT</code> rotates the top three stack items.</li>
          <li><code>2DUP</code> duplicates a two-cell pair.</li>
          <li><code>2DROP</code> removes a two-cell pair.</li>
          <li><code>2SWAP</code> swaps the top two pairs.</li>
          <li><code>2OVER</code> and <code>2ROT</code> extend pair operations further down the stack.</li>
          <li><code>PICK</code> duplicates an indexed stack item, and <code>ROLL</code> moves it to the top.</li>
          <li><code>WITHIN</code> checks whether a value is inside a low/high range.</li>
          <li><code>DEPTH</code> pushes the current stack depth.</li>
          <li><code>.S</code> prints the whole stack without consuming it.</li>
        </ul>
        <p><code>.S</code> is especially useful while learning. It turns invisible stack state into visible text.</p>
      `
    },
    {
      id: "forth-lesson-4",
      summary:
        "Control how numbers are read and printed, including signed literals and hexadecimal input that stays useful for machine work.",
      title: "Lesson 4: Signed Literals, HEX, DECIMAL, and Dot Output",
      bodyHtml: `
        <p>FORTH lets you change the current number base. This matters because addresses and masks are often clearer in hex, while counts and results are often clearer in decimal.</p>
        <pre><code>DECIMAL 15 .
HEX F .
$2A .
DECIMAL -1 .
+7 .
-$2A .</code></pre>
        <p>Key rules:</p>
        <ul>
          <li><code>DECIMAL</code> switches parsing and <code>.</code> output to base 10.</li>
          <li><code>HEX</code> switches them to base 16.</li>
          <li><code>$</code>-prefixed hex literals remain available regardless of the current base.</li>
          <li>Signed literals may start with <code>+</code> or <code>-</code>.</li>
        </ul>
        <p>The point is not only convenience. It is about using the right representation for the job in front of you.</p>
      `
    },
    {
      id: "forth-lesson-5",
      summary:
        "Use the arithmetic core and the new everyday numeric helpers so simple calculations stop turning into stack puzzles.",
      title: "Lesson 5: Arithmetic, /MOD, 1+/1-, NEGATE, ABS, MIN, and MAX",
      bodyHtml: `
        <p>This lesson groups the arithmetic words you will use constantly.</p>
        <pre><code>10 3 - .
6 7 * .
14 5 /MOD . .
5 1+ .
5 1- .
-5 NEGATE .
-5 ABS .
2 3 MIN .
2 3 MAX .</code></pre>
        <ul>
          <li>Basic arithmetic: <code>+</code>, <code>-</code>, <code>*</code>, <code>/</code>, <code>MOD</code>, <code>/MOD</code>.</li>
          <li>Small step helpers: <code>1+</code> and <code>1-</code>.</li>
          <li>Sign and range helpers: <code>NEGATE</code>, <code>ABS</code>, <code>MIN</code>, <code>MAX</code>.</li>
        </ul>
        <p>These are small words, but they keep many real definitions shorter and easier to understand.</p>
      `
    },
    {
      id: "forth-lesson-6",
      summary:
        "Compute flags, combine them with logic, and use bitwise tools that are natural in low-level machine work.",
      title: "Lesson 6: Comparisons, Flags, Logic, INVERT/NOT, and Shifts",
      bodyHtml: `
        <p>FORTH uses ordinary stack values as flags. In this system, false is <code>0</code> and true is typically <code>-1</code>.</p>
        <pre><code>5 5 = .
2 3 &lt; .
3 2 &gt; .
0 0= .
-1 0&lt; .
6 3 AND .
6 3 OR .
6 3 XOR .
$FF00 INVERT .
$FF00 NOT .
1 4 LSHIFT .
$10 1 RSHIFT .</code></pre>
        <p>This lesson explicitly introduces:</p>
        <ul>
          <li>Comparisons: <code>=</code>, <code>&lt;</code>, <code>&gt;</code>, <code>0=</code>, <code>0&lt;</code>.</li>
          <li>Bitwise logic: <code>AND</code>, <code>OR</code>, <code>XOR</code>, <code>INVERT</code>, and the alias <code>NOT</code>.</li>
          <li>Shifts: <code>LSHIFT</code> and <code>RSHIFT</code>.</li>
        </ul>
        <p>These words become especially valuable once you start touching memory-mapped registers and machine-oriented data.</p>
      `
    },
    {
      id: "forth-lesson-7",
      summary:
        "Produce readable text with both low-level and higher-level output words, including the system's visible Hello, world! example.",
      title: "Lesson 7: CR, EMIT, SPACE, SPACES, TYPE, and Hello, world!",
      bodyHtml: `
        <p>FORTH gives you direct control over text output. That is educational, not accidental.</p>
        <pre><code>CR
65 EMIT SPACE 66 EMIT 3 SPACES 67 EMIT CR

: HELLO
  CR ." Hello, world!" CR
;

HELLO</code></pre>
        <p>Useful output words include:</p>
        <ul>
          <li><code>CR</code> for a new line.</li>
          <li><code>EMIT</code> for a single character code.</li>
          <li><code>SPACE</code> and <code>SPACES</code> for formatting.</li>
          <li><code>TYPE</code> for printing text from an address/length pair.</li>
          <li><code>."</code> for in-line string output.</li>
          <li><code>S"</code> for producing an <code>addr len</code> string pair instead of printing immediately.</li>
        </ul>
        <p>The low-level and high-level forms belong together. The first teaches what text output really is; the second lets you work quickly once you understand that model.</p>
      `
    },
    {
      id: "forth-lesson-8",
      summary:
        "Understand how the AM8 FORTH editor stores source and why LIST, LINE, RUN, and EXIT are part of the daily workflow.",
      title: "Lesson 8: FORTH, LIST, RUN, LINE, and EXIT",
      bodyHtml: `
        <p>The AM8 FORTH environment combines a live interactive session with a line-based ROM source buffer. That makes it feel different from both classic BASIC and from a modern text file plus compiler setup.</p>
        <ul>
          <li><code>FORTH</code> enters FORTH mode.</li>
          <li><code>LIST</code> prints the stored program.</li>
          <li><code>RUN</code> executes the stored program.</li>
          <li><code>LINE n ...</code> explicitly replaces or deletes one stored line.</li>
          <li><code>SAVE "NAME"</code> stores the current shared language image to disk, and <code>LOAD "NAME"</code> restores it later.</li>
          <li><code>EXIT</code> leaves FORTH mode and returns to the plain shell.</li>
        </ul>
        <p>In FORTH mode, unnumbered non-command input is interpreted immediately and is <strong>not</strong> automatically saved. <code>LINE</code> and <code>LIST</code> manage the explicit replay/save source buffer, and <code>RUN</code> replays that explicit buffer from a fresh session when it contains stored source.</p>
        <pre><code>LINE 10 : SQUARE DUP * ;
SAVE "SQUARE" OVERWRITE
LOAD "SQUARE"
LIST</code></pre>
        <p>That disk step matters because the live FORTH session and the explicit saved source buffer are different things. <code>SAVE</code> captures the current language image for later work, and <code>LOAD</code> brings it back so <code>LIST</code> and <code>RUN</code> can pick up where you left off.</p>
      `
    },
    {
      id: "forth-lesson-9",
      summary:
        "Read and write raw machine memory so FORTH starts to feel like a systems language instead of only a stack calculator.",
      title: "Lesson 9: @, !, C@, C!, BSET, BCLR, and BTEST",
      bodyHtml: `
        <p>Memory access is one of FORTH's strengths. This system includes 16-bit cell access, byte access, and AM8-specific bit helpers.</p>
        <pre><code>$2A $5000 C!
$5000 C@ .
$1234 $6000 !
$6000 @ .
$01 $D020 BSET
$01 $D020 BTEST .
$01 $D020 BCLR</code></pre>
        <ul>
          <li><code>@</code> fetches a 16-bit cell.</li>
          <li><code>!</code> stores a 16-bit cell.</li>
          <li><code>C@</code> fetches one byte.</li>
          <li><code>C!</code> stores one byte.</li>
          <li><code>BSET</code>, <code>BCLR</code>, and <code>BTEST</code> update or test individual bits in a byte.</li>
        </ul>
        <p>Be careful with machine addresses. Direct access is powerful because it changes live system state immediately.</p>
      `
    },
    {
      id: "forth-lesson-10",
      summary:
        "Use FORTH comments deliberately so experiments stay readable and temporary notes do not turn into broken source.",
      title: "Lesson 10: Backslash and Parenthesized Comments",
      bodyHtml: `
        <p>This FORTH supports two important comment styles.</p>
        <pre><code>1 2 + \ ignore the rest of this line

1 ( temporary note ) 2 + .</code></pre>
        <ul>
          <li><code>\</code> ignores the rest of the current source line.</li>
          <li><code>( ... )</code> ignores everything until the closing parenthesis on the current line.</li>
        </ul>
        <p>These are not decoration. They make iterative experiments and teaching examples much easier to follow.</p>
      `
    },
    {
      id: "forth-lesson-11",
      summary:
        "Turn one-off word sequences into reusable vocabulary and see why colon definitions are the normal way to grow a FORTH program.",
      title: "Lesson 11: Colon Definitions, Factoring, and EXIT",
      bodyHtml: `
        <p>Real FORTH becomes powerful when you stop typing everything as one-off sequences and start defining new words.</p>
        <pre><code>: TWICE DUP + ;
5 TWICE .

: SIGN-LABEL
  DUP 0&lt; IF ." NEGATIVE" EXIT THEN
  ." ZERO OR POSITIVE"
;</code></pre>
        <p>The most important habit here is <strong>factoring</strong>: break a problem into small named words instead of writing one giant definition.</p>
      `
    },
    {
      id: "forth-lesson-12",
      summary:
        "Choose between branches by computing flags first and then consuming them with IF, ELSE, and THEN.",
      title: "Lesson 12: IF, ELSE, and THEN",
      bodyHtml: `
        <p>FORTH conditionals do not wrap a separate boolean syntax. You compute a flag first, then <code>IF</code> consumes it.</p>
        <pre><code>: SIGN-TEXT
  DUP 0&lt;
  IF
    ." NEGATIVE"
  ELSE
    ." ZERO OR POSITIVE"
  THEN
  CR
;</code></pre>
        <p>The structure is compact, but the idea is simple: leave a flag on the stack, branch with <code>IF</code>, optionally use <code>ELSE</code>, and close with <code>THEN</code>.</p>
      `
    },
    {
      id: "forth-lesson-13",
      summary:
        "Use the BEGIN-family loops when repetition should depend on a condition instead of a fixed counter.",
      title: "Lesson 13: BEGIN, UNTIL, AGAIN, WHILE, and REPEAT",
      bodyHtml: `
        <p>The <code>BEGIN</code> family is useful when a loop is controlled by a condition rather than a fixed range.</p>
        <pre><code>: INC3 0 BEGIN 1+ DUP 3 = UNTIL ;
: SPIN  BEGIN AGAIN ;
: COUNT2 0 BEGIN DUP 2 &lt; WHILE 1+ REPEAT ;</code></pre>
        <ul>
          <li><code>BEGIN ... UNTIL</code> repeats until the flag becomes true.</li>
          <li><code>BEGIN ... AGAIN</code> loops forever.</li>
          <li><code>BEGIN ... WHILE ... REPEAT</code> splits the loop into a test and a body.</li>
        </ul>
      `
    },
    {
      id: "forth-lesson-14",
      summary:
        "Use counted loops, custom steps, and loop indices so repeated numeric work stays compact and explicit.",
      title: "Lesson 14: DO, ?DO, LOOP, +LOOP, LEAVE, I, and J",
      bodyHtml: `
        <p>Counted loops are the second major FORTH loop style. They are often easier for beginners because the start and limit are explicit.</p>
        <pre><code>: TABLE5 11 1 DO I 5 * . LOOP ;
: STEP3 0 10 0 DO I 6 = IF LEAVE THEN I . 1 + 2 +LOOP ;
: MAYBE-RUN 5 5 ?DO I . LOOP ;</code></pre>
        <p>This lesson explicitly covers <code>DO</code>, <code>?DO</code>, <code>LOOP</code>, <code>+LOOP</code>, <code>LEAVE</code>, <code>I</code>, and <code>J</code>.</p>
      `
    },
    {
      id: "forth-lesson-15",
      summary:
        "Move values through the return stack and understand why it is useful but more dangerous than ordinary data-stack work.",
      title: "Lesson 15: >R, R>, and R@",
      bodyHtml: `
        <p>FORTH has both a data stack and a return stack. The return stack normally manages calls and loops, but the language also exposes it directly.</p>
        <pre><code>$1234 &gt;R
R@ .
R&gt; .</code></pre>
        <ul>
          <li><code>&gt;R</code> moves a value from the data stack to the return stack.</li>
          <li><code>R@</code> copies the top return-stack value without removing it.</li>
          <li><code>R&gt;</code> moves a value back to the data stack.</li>
        </ul>
        <p>Use these words carefully. They are powerful because they give you extra temporary storage, but they also make control flow easier to damage if you lose track of state.</p>
      `
    },
    {
      id: "forth-lesson-16",
      summary:
        "Parse words and gather real interactive input so FORTH stops feeling static and starts reacting to typed text.",
      title: "Lesson 16: WORD, KEY, ACCEPT, EXPECT, QUERY, and S\"",
      bodyHtml: `
        <p>These words turn FORTH into something more interactive than a fixed stored program.</p>
        <pre><code>44 WORD HELLO,
KEY
$5000 8 ACCEPT
$5000 8 EXPECT
QUERY
S" HI" TYPE</code></pre>
        <ul>
          <li><code>WORD</code> parses the next token using the supplied delimiter character and returns the buffer address.</li>
          <li><code>KEY</code> reads one interactive character-style input unit.</li>
          <li><code>ACCEPT</code> reads text into an explicit buffer and leaves the resulting length.</li>
          <li><code>EXPECT</code> reads a line of input into an explicit address/length buffer.</li>
          <li><code>QUERY</code> reads a line of input into the shared line buffer used by the interpreter.</li>
          <li><code>S" ..."</code> produces a string as <code>addr len</code> so you can pass it to words like <code>TYPE</code>.</li>
        </ul>
        <p>This matters because it means the current AM8 FORTH no longer only accepts fixed stored lines. It can also ask for new text while it runs.</p>
      `
    },
    {
      id: "forth-lesson-17",
      summary:
        "Work directly with the dictionary data space so FORTH can store not only behavior but also structured raw data.",
      title: "Lesson 17: HERE, ALLOT, comma, C, and COUNT",
      bodyHtml: `
        <p>FORTH is not only about the stack. It is also about a live dictionary and data space.</p>
        <pre><code>HERE .
2 ALLOT
HERE DUP &gt;R $1234 , $56 C,
R&gt; DUP @ . 2 + C@ .

$02 $5010 C! $48 $5011 C! $49 $5012 C!
$5010 COUNT TYPE</code></pre>
        <ul>
          <li><code>HERE</code> leaves the current dictionary pointer.</li>
          <li><code>ALLOT</code> moves that pointer forward by a signed count.</li>
          <li><code>,</code> stores one 16-bit cell into dictionary space.</li>
          <li><code>C,</code> stores one byte into dictionary space.</li>
          <li><code>COUNT</code> turns a counted string into <code>addr+1 len</code>.</li>
        </ul>
        <p>This is one of the points where FORTH starts to feel like a language for building your own little runtime structures.</p>
      `
    },
    {
      id: "forth-lesson-18",
      summary:
        "Define words that behave like named values or named storage locations instead of only named procedures.",
      title: "Lesson 18: CONSTANT and VARIABLE",
      bodyHtml: `
        <p>FORTH lets you define more than reusable behavior. It also lets you define value words and storage words.</p>
        <pre><code>10 CONSTANT TEN
VARIABLE COUNT
TEN .
5 COUNT !
COUNT @ .</code></pre>
        <ul>
          <li><code>CONSTANT</code> defines a word that leaves its captured value when executed.</li>
          <li><code>VARIABLE</code> defines a word that leaves an address so <code>@</code> and <code>!</code> can access the stored cell.</li>
        </ul>
      `
    },
    {
      id: "forth-lesson-19",
      summary:
        "Move from simple named storage to defining words that generate new runtime behavior.",
      title: "Lesson 19: CREATE and DOES>",
      bodyHtml: `
        <p><code>CREATE</code> and <code>DOES&gt;</code> are the point where FORTH stops feeling fixed and starts feeling programmable at the language level.</p>
        <pre><code>CREATE BOX
BOX .

CREATE LABEL DOES&gt; DROP $1234
LABEL .</code></pre>
        <p><code>CREATE</code> gives you a new header and parameter area. <code>DOES&gt;</code> replaces the default parameter-address behavior with custom runtime behavior.</p>
      `
    },
    {
      id: "forth-lesson-20",
      summary:
        "Learn the compile-time control words that make FORTH feel meta-programmable instead of frozen.",
      title: "Lesson 20: IMMEDIATE, [ ], LITERAL, [COMPILE], LIT, and EXIT",
      bodyHtml: `
        <p>This is the most advanced beginner lesson in the course. These words control how definitions behave while they are compiled.</p>
        <pre><code>: FLAG $2A $500E C! ; IMMEDIATE
: BRK [ 1 2 + ] LITERAL ;
: GIVE LIT $2A EXIT LIT $2B ;
: FORCE-IF [COMPILE] IF ;</code></pre>
        <ul>
          <li><code>IMMEDIATE</code> makes a word run during compilation.</li>
          <li><code>[</code> and <code>]</code> switch compilation behavior.</li>
          <li><code>LITERAL</code> embeds a value computed at compile time.</li>
          <li><code>[COMPILE]</code> forces compilation behavior for the next word.</li>
          <li><code>LIT</code> and <code>EXIT</code> are low-level runtime building blocks.</li>
        </ul>
      `
    },
    {
      id: "forth-lesson-21",
      summary:
        "Use execution tokens and dictionary search words so FORTH can inspect and run words indirectly.",
      title: "Lesson 21: Tick, EXECUTE, FIND, WORDS, >BODY, BODY>, SEE, and FORGET",
      bodyHtml: `
        <p>These words move you from ordinary calls into indirect execution and dictionary introspection.</p>
        <pre><code>: HI $2A ;
' HI EXECUTE .
44 WORD HI, FIND .S
WORDS
' HI >BODY BODY>
SEE HI
FORGET HI</code></pre>
        <ul>
          <li><code>'</code> parses the next word and leaves an execution token.</li>
          <li><code>EXECUTE</code> runs the token on the stack.</li>
          <li><code>FIND</code> looks up a parsed name and reports whether it exists.</li>
          <li><code>WORDS</code> lists the current dictionary words.</li>
          <li><code>>BODY</code> and <code>BODY></code> move between a user word's execution token and its current body location.</li>
          <li><code>SEE</code> gives lightweight word introspection, and <code>FORGET</code> trims the dictionary back to the named word.</li>
        </ul>
        <p>This is one of the clearest signs that FORTH is a language for building and reflecting on its own vocabulary.</p>
      `
    },
    {
      id: "forth-lesson-22",
      summary:
        "Use the low-level memory block words to fill, copy, clear, and inspect raw memory ranges directly from FORTH.",
      title: "Lesson 22: FILL, MOVE, CMOVE, ERASE, and DUMP",
      bodyHtml: `
        <p>These words make FORTH much more practical for memory-oriented work.</p>
        <pre><code>$5000 8 $AA FILL
$5000 $5008 8 CMOVE
$5000 $5001 8 MOVE
$5000 8 DUMP
$5000 8 ERASE</code></pre>
        <ul>
          <li><code>FILL</code> writes one byte across a range.</li>
          <li><code>CMOVE</code> copies forward.</li>
          <li><code>MOVE</code> handles overlapping ranges safely.</li>
          <li><code>DUMP</code> prints a compact hex view.</li>
          <li><code>ERASE</code> clears a range to zero.</li>
        </ul>
      `
    },
    {
      id: "forth-lesson-23",
      summary:
        "Finish with the control cells and escape words that make a live FORTH session more self-aware and easier to recover.",
      title: "Lesson 23: ABORT, QUIT, BASE, STATE, and REPL",
      bodyHtml: `
        <p>The AM8 FORTH system now has both a persistent live session and a separate explicit source buffer. These words help you control that environment.</p>
        <pre><code>HEX BASE @ .
STATE @ .
1 2 ABORT
1 2 QUIT
REPL
5 DUP + .

</code></pre>
        <ul>
          <li><code>BASE</code> exposes the current base cell so <code>BASE @</code> and <code>BASE !</code> work naturally.</li>
          <li><code>STATE</code> exposes the current compile-state cell.</li>
          <li><code>ABORT</code> clears the current interactive execution state.</li>
          <li><code>QUIT</code> clears the current interactive execution state and returns to the outer prompt flow.</li>
          <li><code>REPL</code> enters a simple line-at-a-time interactive loop inside FORTH until a blank line exits it.</li>
        </ul>
        <p>At this point you have seen every major FORTH feature currently implemented in the system: stack work, arithmetic, memory, definitions, compile control, data space, indirect execution, comments, input, and interactive workflow. The best next step is not more reading. It is writing many tiny words and testing them constantly.</p>
        <div class="manuals-callout">
          <p><strong>Coverage checklist:</strong> this tutorial now explicitly covers the shipped FORTH surface.</p>
          <ul>
            <li><strong>Workflow:</strong> <code>FORTH</code>, <code>LIST</code>, <code>LINE</code>, <code>RUN</code>, active-mode <code>SAVE</code>/<code>LOAD</code>, <code>EXIT</code>, <code>REPL</code>.</li>
            <li><strong>Stack and inspection:</strong> <code>DUP</code>, <code>DROP</code>, <code>SWAP</code>, <code>OVER</code>, <code>NIP</code>, <code>ROT</code>, <code>2DUP</code>, <code>2DROP</code>, <code>2SWAP</code>, <code>2OVER</code>, <code>2ROT</code>, <code>PICK</code>, <code>ROLL</code>, <code>WITHIN</code>, <code>DEPTH</code>, <code>.S</code>.</li>
            <li><strong>Numbers and base:</strong> signed literals, <code>HEX</code>, <code>DECIMAL</code>, <code>.</code>.</li>
            <li><strong>Arithmetic and logic:</strong> <code>+</code>, <code>-</code>, <code>*</code>, <code>/</code>, <code>MOD</code>, <code>/MOD</code>, <code>1+</code>, <code>1-</code>, <code>NEGATE</code>, <code>ABS</code>, <code>MIN</code>, <code>MAX</code>, <code>=</code>, <code>&lt;</code>, <code>&gt;</code>, <code>0=</code>, <code>0&lt;</code>, <code>AND</code>, <code>OR</code>, <code>XOR</code>, <code>INVERT</code>/<code>NOT</code>, <code>LSHIFT</code>, <code>RSHIFT</code>.</li>
            <li><strong>Memory and output:</strong> <code>@</code>, <code>!</code>, <code>C@</code>, <code>C!</code>, <code>BSET</code>, <code>BCLR</code>, <code>BTEST</code>, <code>FILL</code>, <code>MOVE</code>, <code>CMOVE</code>, <code>ERASE</code>, <code>DUMP</code>, <code>CR</code>, <code>EMIT</code>, <code>SPACE</code>, <code>SPACES</code>, <code>TYPE</code>, <code>."</code>, <code>S"</code>.</li>
            <li><strong>Control flow and return stack:</strong> <code>IF</code>, <code>ELSE</code>, <code>THEN</code>, <code>BEGIN</code>, <code>UNTIL</code>, <code>AGAIN</code>, <code>WHILE</code>, <code>REPEAT</code>, <code>?DO</code>, <code>DO</code>, <code>LOOP</code>, <code>+LOOP</code>, <code>LEAVE</code>, <code>I</code>, <code>J</code>, <code>&gt;R</code>, <code>R&gt;</code>, <code>R@</code>.</li>
            <li><strong>Defining and metaprogramming:</strong> <code>:</code>, <code>;</code>, <code>IMMEDIATE</code>, <code>[</code>, <code>]</code>, <code>LITERAL</code>, <code>[COMPILE]</code>, <code>LIT</code>, <code>CONSTANT</code>, <code>VARIABLE</code>, <code>CREATE</code>, <code>DOES&gt;</code>, <code>HERE</code>, <code>ALLOT</code>, <code>,</code>, <code>C,</code>, <code>COUNT</code>, <code>'</code>, <code>EXECUTE</code>, <code>FIND</code>, <code>WORDS</code>, <code>&gt;BODY</code>, <code>BODY&gt;</code>, <code>SEE</code>, <code>FORGET</code>.</li>
            <li><strong>Parsing, input, and control cells:</strong> <code>WORD</code>, <code>KEY</code>, <code>ACCEPT</code>, <code>EXPECT</code>, <code>QUERY</code>, <code>ABORT</code>, <code>QUIT</code>, <code>BASE</code>, <code>STATE</code>, <code>\</code>, <code>( ... )</code>.</li>
          </ul>
        </div>
        <div class="manuals-callout">
          <p><strong>Learning strategy:</strong> do not wait until you "understand all of FORTH" before writing your own words. In FORTH, understanding grows by building, running, inspecting, and refining very small definitions.</p>
        </div>
      `
    }
  ]
};
